import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "../utils/apiRequest";
import { showToast } from "../utils/toastOptions";

const BASE = process.env.REACT_APP_API_BASE_URL;

export default function DistributionGroupModal({
  open,
  onClose,
  companyId,
  selectedUserIds = [],
  companyUsers = [],
  projects = [],
}) {
  const [mode, setMode] = useState("create");
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Create mode
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);

  // Add-to-existing mode
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Step 2: users not on project
  const [step, setStep] = useState(1);
  const [missingUsers, setMissingUsers] = useState([]);
  const [addToProject, setAddToProject] = useState(true);
  const [checkingMembers, setCheckingMembers] = useState(false);
  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState(null);

  const selectedUserNames = useMemo(() => {
    const idSet = new Set(selectedUserIds.map(Number));
    return companyUsers
      .filter((u) => idSet.has(u.id))
      .map((u) => u.name || u.email_address || `User ${u.id}`)
      .slice(0, 8);
  }, [selectedUserIds, companyUsers]);

  const fetchGroups = useCallback(async () => {
    if (!companyId) return;
    setLoadingGroups(true);
    try {
      const data = await apiRequest(
        `${BASE}/companies/${companyId}/distribution-groups/`,
        { method: "GET" }
      );
      setGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast("error", e.message || "Failed to load distribution groups");
    } finally {
      setLoadingGroups(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (open) {
      fetchGroups();
      setMode("create");
      setGroupName("");
      setGroupDescription("");
      setSelectedProject(null);
      setSelectedGroup(null);
      setStep(1);
      setMissingUsers([]);
      setAddToProject(true);
    }
  }, [open, fetchGroups]);

  const getProjectId = () => {
    if (mode === "create") return selectedProject?.id;
    return selectedGroup?.project_id;
  };

  const checkProjectMembership = async () => {
    const projectId = getProjectId();
    if (!projectId) return;
    setCheckingMembers(true);
    try {
      const data = await apiRequest(
        `${BASE}/companies/${companyId}/projects/${projectId}/users/`,
        { method: "GET", suppressErrorToast: true }
      );
      const projectUserIds = new Set(
        (data?.project_users || []).map((u) => u.id)
      );
      const idSet = new Set(selectedUserIds.map(Number));
      const missing = companyUsers.filter(
        (u) => idSet.has(u.id) && !projectUserIds.has(u.id)
      );
      if (missing.length > 0) {
        setMissingUsers(missing);
        setStep(2);
      } else {
        await doSubmit();
      }
    } catch {
      await doSubmit();
    } finally {
      setCheckingMembers(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    try {
      await apiRequest(
        `${BASE}/companies/${companyId}/distribution-groups/${group.id}/delete/`,
        { method: "DELETE", body: { project_id: group.project_id, group_name: group.name } }
      );
      showToast("success", `Deleted distribution group "${group.name}"`);
      setConfirmDeleteGroup(null);
      if (selectedGroup?.id === group.id) setSelectedGroup(null);
      fetchGroups();
    } catch (e) {
      showToast("error", e.message || "Failed to delete distribution group");
    }
  };

  const doSubmit = async () => {
    const missingIdSet = new Set(missingUsers.map((u) => u.id));
    const shouldExclude = !addToProject && missingIdSet.size > 0;
    const filteredIds = shouldExclude
      ? selectedUserIds.filter((id) => !missingIdSet.has(Number(id)))
      : selectedUserIds;

    setSubmitting(true);
    try {
      if (mode === "create") {
        await apiRequest(
          `${BASE}/companies/${companyId}/distribution-groups/create/`,
          {
            method: "POST",
            body: {
              project_id: selectedProject.id,
              name: groupName.trim(),
              description: groupDescription.trim(),
              user_ids: filteredIds,
            },
          }
        );
        showToast(
          "success",
          `Created "${groupName.trim()}" with ${filteredIds.length} user${
            filteredIds.length === 1 ? "" : "s"
          }`
        );
      } else {
        const existingIds = (selectedGroup.users || []).map((u) =>
          typeof u === "object" ? u.id : u
        );
        const mergedIds = [
          ...new Set([...existingIds, ...filteredIds.map(Number)]),
        ];
        await apiRequest(
          `${BASE}/companies/${companyId}/distribution-groups/${selectedGroup.id}/update/`,
          {
            method: "PATCH",
            body: {
              project_id: selectedGroup.project_id,
              user_ids: mergedIds,
            },
          }
        );
        const addedCount = mergedIds.length - existingIds.length;
        showToast(
          "success",
          `Added ${addedCount} user${addedCount === 1 ? "" : "s"} to "${
            selectedGroup.name
          }" (${mergedIds.length} total)`
        );
      }
      onClose();
    } catch (e) {
      showToast("error", e.message || "Failed to save distribution group");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStep2Confirm = async () => {
    if (addToProject) {
      setSubmitting(true);
      try {
        const projectId = getProjectId();
        const pairs = missingUsers.map((u) => ({
          user_id: u.id,
          project_id: projectId,
        }));
        await apiRequest(
          `${BASE}/companies/${companyId}/users/bulk_add/`,
          { method: "POST", body: { users: pairs } }
        );
        showToast(
          "success",
          `Added ${missingUsers.length} user${missingUsers.length === 1 ? "" : "s"} to the project`
        );
      } catch (e) {
        showToast("error", e.message || "Failed to add users to project");
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }
    await doSubmit();
  };

  const canSubmit =
    mode === "create"
      ? groupName.trim() && selectedProject && !submitting && !checkingMembers
      : selectedGroup && !submitting && !checkingMembers;

  const projectLabel = mode === "create"
    ? selectedProject?.name
    : selectedGroup?.project_name || `Project ${selectedGroup?.project_id}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {step === 1 ? "Distribution Group" : "Users Not on Project"}
      </DialogTitle>
      <DialogContent dividers>
        {step === 1 && (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {selectedUserIds.length} user{selectedUserIds.length === 1 ? "" : "s"} selected
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {selectedUserNames.map((name) => (
                  <Chip key={name} label={name} size="small" variant="outlined" />
                ))}
                {selectedUserIds.length > 8 && (
                  <Chip
                    label={`+${selectedUserIds.length - 8} more`}
                    size="small"
                    color="default"
                  />
                )}
              </Stack>
            </Box>

            <Divider />

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
              fullWidth
            >
              <ToggleButton value="create">Create New Group</ToggleButton>
              <ToggleButton value="existing" disabled={groups.length === 0 && !loadingGroups}>
                Add to Existing
              </ToggleButton>
            </ToggleButtonGroup>

            {mode === "create" && (
              <>
                <TextField
                  label="Group Name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  autoFocus
                />
                <TextField
                  label="Description"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                />
                <Autocomplete
                  options={projects}
                  getOptionLabel={(p) => p.name || `Project ${p.id}`}
                  value={selectedProject}
                  onChange={(_, v) => setSelectedProject(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Project (group is scoped to a project)"
                      size="small"
                      required
                    />
                  )}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                />
              </>
            )}

            {mode === "existing" && (
              <>
                {loadingGroups ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : groups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                    No existing distribution groups found. Create one first.
                  </Typography>
                ) : (
                  <Autocomplete
                    options={groups}
                    getOptionLabel={(g) => g.name || `Group ${g.id}`}
                    value={selectedGroup}
                    onChange={(_, v) => setSelectedGroup(v)}
                    renderOption={(props, g) => (
                      <li {...props} key={g.id}>
                        <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                          <Stack sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2">{g.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(g.users || []).length} member{(g.users || []).length === 1 ? "" : "s"}
                              {g.project_name ? ` — ${g.project_name}` : ""}
                              {g.description ? ` — ${g.description}` : ""}
                            </Typography>
                          </Stack>
                          <Tooltip title="Delete group">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setConfirmDeleteGroup(g);
                              }}
                              sx={{ ml: 1, flexShrink: 0 }}
                            >
                              <RemoveCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select a distribution group"
                        size="small"
                      />
                    )}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    autoFocus
                  />
                )}
              </>
            )}
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            <Typography variant="body2">
              {missingUsers.length} of the selected user{missingUsers.length === 1 ? " is" : "s are"} not
              currently on <strong>{projectLabel}</strong>:
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 240, overflow: "auto" }}>
              {missingUsers.map((u) => (
                <ListItem key={u.id} disableGutters>
                  <ListItemText
                    primary={u.name || u.email_address || `User ${u.id}`}
                    secondary={u.email_address}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
            <Divider />
            <FormControlLabel
              control={
                <Checkbox
                  checked={addToProject}
                  onChange={(e) => setAddToProject(e.target.checked)}
                />
              }
              label={`Add these users to the project first, then ${mode === "create" ? "create the group" : "add to the group"}`}
            />
            {!addToProject && (
              <Typography variant="caption" color="warning.main">
                The group will be created without these users. Procore requires all distribution group
                members to be on the project.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 2 && (
          <Button onClick={() => { setStep(1); setMissingUsers([]); }} disabled={submitting}>
            Back
          </Button>
        )}
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {step === 1 && (
          <Button
            variant="contained"
            onClick={checkProjectMembership}
            disabled={!canSubmit}
          >
            {checkingMembers ? (
              <CircularProgress size={20} color="inherit" />
            ) : mode === "create" ? (
              "Create Group"
            ) : (
              "Add to Group"
            )}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            onClick={handleStep2Confirm}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : addToProject ? (
              `Add to Project & ${mode === "create" ? "Create" : "Update"}`
            ) : (
              `${mode === "create" ? "Create" : "Update"} Without Them`
            )}
          </Button>
        )}
      </DialogActions>
      {/* Delete group confirmation */}
      <Dialog
        open={!!confirmDeleteGroup}
        onClose={() => setConfirmDeleteGroup(null)}
        maxWidth="xs"
      >
        <DialogTitle>Delete Distribution Group</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{confirmDeleteGroup?.name}</strong>
            {confirmDeleteGroup?.project_name ? ` from ${confirmDeleteGroup.project_name}` : ""}?
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteGroup(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDeleteGroup(confirmDeleteGroup)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}